import { config } from "@nova/configuration/browser"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { IAgoraRTCClient, IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng"
import {
    LiveVideoStatus,
    selectLiveVideoFlightById,
    selectLiveVideoFlightIdsOrderedByDateCreated,
    selectLiveVideoStatus,
} from "features/entity-caching/flights/selectors"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { subToFlightEvents, unsubToFlightEvents } from "features/pubsub/sockets/drones"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { fetchTelemetryEvents } from "tools/telemetry/fetchTelemetryEvents"
import store from "integrations/redux/store"
import { safeImportAgora } from "../LiveVideo/agoraHelper"
import { createRemoteAudioPlayback } from "../LiveVideo/remoteAudioPlayback"
import { AbstractVideoController, VideoControllerData } from "./AbstractVideoController"
import {
    startListeningToFlightChanges,
    flightListenerEventEmitter,
} from "./hooks/flightListenerMiddleware"
import { VideoControllerFactory } from "./VideoControllerFactory"
import { fixBadTelemetryData } from "../module"

type AgoraUser = IAgoraRTCRemoteUser

// https://api-ref.agora.io/en/video-sdk/web/4.x/enums/audiencelatencyleveltype.html
const AUDIENCE_LEVEL_ULTRA_LOW_LATENCY = 2

export class LiveVideoFlightLiveController extends AbstractVideoController {
    private client?: IAgoraRTCClient
    private agoraUser?: IAgoraRTCRemoteUser
    private mediaStreamTrack?: MediaStreamTrack
    private audioPlayback = createRemoteAudioPlayback()
    private streamIsInitialized = false
    private waitingForStreamToStart = true

    constructor({ flightId }: { flightId: string }) {
        super({ flightId })
        const AgoraRTC = safeImportAgora()
        if (!AgoraRTC) {
            throw new Error("Tried to create Agora Client while Agora is unavailable")
        }
        this.client = AgoraRTC.createClient({ mode: "live", codec: "h264", role: "audience" })
        const orderedLiveFlightIds = selectLiveVideoFlightIdsOrderedByDateCreated(store.getState())
        const thisFlightIndex = orderedLiveFlightIds.findIndex((flight) => flight.id === flightId)
        if (thisFlightIndex === -1) {
            throw new Error("Initialized LiveVideoController for live flight that does not exist")
        }
        const flight = selectLiveVideoFlightById(flightId)(store.getState())
        this.waitingForStreamToStart = !flight.streamCreated
        this.droneIconColor = droneIconColors[thisFlightIndex % droneIconColors.length]
        this.videoProgress = 1
        this.stopListeningToFlightChanges = startListeningToFlightChanges({
            flightId: this.flightId,
        })
        this.boundHandleFlightChange = this.handleFlightChange.bind(this)
        flightListenerEventEmitter.on(this.flightId, this.boundHandleFlightChange)
    }

    override async initialize(signal: AbortSignal) {
        const setup = async () => {
            if (!this.client) return
            if (!this.streamIsInitialized && !this.waitingForStreamToStart) {
                this.off("videoAvailabilityChanged", setup)
                await this.client.setClientRole("audience", {
                    level: AUDIENCE_LEVEL_ULTRA_LOW_LATENCY,
                })
                if (signal.aborted) {
                    console.warn("Aborted before initialization")
                    await this.deinitialize()
                    return
                }
                this.boundOnPublishAgoraEvent = this.onPublishAgoraEvent.bind(this)
                this.boundOnUnpublishAgoraEvent = this.onUnpublishAgoraEvent.bind(this)
                this.client.on("user-published", this.boundOnPublishAgoraEvent)
                this.client.on("user-unpublished", this.boundOnUnpublishAgoraEvent)
                const res = await fetch(`/api/live-video/${this.flightId}/get-watch-token`)
                const { token } = await res.json()
                await this.client.join(config.get("agoraId"), this.flightId, token, null)
                store.dispatch(subToFlightEvents(this.flightId))
                this.streamIsInitialized = true
            }
            if (signal.aborted) {
                console.warn("Aborted after initialization, deinitializing...")
                await this.deinitialize()
            }
        }
        this.on("videoAvailabilityChanged", setup)
        setup()
    }
    override async deinitialize() {
        this.stopListeningToFlightChanges?.()
        flightListenerEventEmitter.off(this.flightId, this.boundHandleFlightChange!)
        if (!this.client) return
        if (this.streamIsInitialized) {
            this.client.off("user-published", this.boundOnPublishAgoraEvent!)
            this.client.off("user-unpublished", this.boundOnUnpublishAgoraEvent!)
            await this.onUnpublishAgoraEvent()
            await this.client.leave()
            store.dispatch(unsubToFlightEvents(this.flightId))
            this.streamIsInitialized = false
        }
    }

    private boundHandleFlightChange?: typeof this.handleFlightChange
    private handleFlightChange() {
        const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
        if (!flight) return
        const isWaiting = !flight.streamCreated
        if (isWaiting !== this.waitingForStreamToStart) {
            this.waitingForStreamToStart = isWaiting
            this.emit("videoAvailabilityChanged")
        }
        if (!flight.live) {
            VideoControllerFactory.tearDownControllerForFlight({ flightId: this.flightId })
        }
    }

    private boundOnPublishAgoraEvent?: typeof this.onPublishAgoraEvent
    private async onPublishAgoraEvent(
        user: AgoraUser,
        mediaType: "video" | "audio" | "datachannel"
    ) {
        if (!this.streamIsInitialized || !this.client) return
        if (mediaType === "video") {
            this.agoraUser = user
            await this.client.subscribe(user, mediaType)
            if (user.videoTrack) {
                this.mediaStreamTrack = user.videoTrack.getMediaStreamTrack()
                this.emit("videoTrackChanged")
            }
            this.refreshConnectivity()
            return
        }
        if (mediaType === "audio") {
            this.agoraUser = user
            this.audioPlayback.attach(await this.client.subscribe(user, mediaType))
            this.audioIsAvailable = true
            this.applyAudioSettings()
            this.emit("audioAvailabilityChanged")
            this.refreshConnectivity()
        }
    }

    private refreshConnectivity() {
        const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
        const streamIsLive = !!flight?.live
        this.setStreamAvailability({
            cameraIsOff: streamIsLive && !this.mediaStreamTrack && this.audioIsAvailable,
            isDisconnected: streamIsLive && !this.mediaStreamTrack && !this.audioIsAvailable,
        })
    }
    private boundOnUnpublishAgoraEvent?: typeof this.onUnpublishAgoraEvent
    private async onUnpublishAgoraEvent(
        _user?: AgoraUser,
        mediaType?: "video" | "audio" | "datachannel"
    ) {
        if (!this.streamIsInitialized || !this.client || !this.agoraUser) return
        if (!mediaType || mediaType === "video") {
            try {
                await this.client.unsubscribe(this.agoraUser, "video")
            } catch {}
            if (this.mediaStreamTrack) {
                this.mediaStreamTrack = undefined
                this.emit("videoTrackChanged")
            }
            this.refreshConnectivity()
        }
        if (!mediaType || mediaType === "audio") {
            this.audioPlayback.detach()
            try {
                await this.client.unsubscribe(this.agoraUser, "audio")
            } catch {}
            if (this.audioIsAvailable) {
                this.audioIsAvailable = false
                this.emit("audioAvailabilityChanged")
            }
            this.refreshConnectivity()
        }
    }
    protected override applyAudioSettings() {
        this.audioPlayback.apply({ muted: this.isMuted, volumePercent: this.volumePercent })
    }

    override attachToElement(element: HTMLVideoElement) {
        this.videoElement = element
        const attach = () => {
            if (this.mediaStreamTrack) {
                element.srcObject = new MediaStream([this.mediaStreamTrack])
                this.isPlaying = true
                this.hasPlayed = true
                this.emit("isPlayingChanged")
            } else {
                // Without this the element holds the last decoded frame instead of going black.
                element.srcObject = null
            }
        }
        this.on("videoTrackChanged", attach)
        attach()
        return () => {
            element.srcObject = null
            element.removeAttribute("srcObject")
            element.load()
            this.off("videoTrackChanged", attach)
        }
    }

    override async fetchAllData(zoomTo?: true) {
        const state = store.getState()
        const flight = selectLiveVideoFlightById(this.flightId)(state)
        const orgId = selectActiveWorkspaceId()(state)
        const projectId = selectActivatedFireId(state)
        const { events } = await fetchTelemetryEvents({
            orgId: orgId!,
            projectId: projectId!,
            id: this.flightId,
            start: dateFromNumber(flight.startedAt).getTime(),
            end: flight.endedAt ? dateFromNumber(flight.endedAt).getTime() : undefined,
        })
        const videoEvents = events.filter((e): e is VideoControllerData => e.type === "liveVideo")
        const sorted = [...videoEvents, ...this.data].toSorted((a, b) => a.timestamp - b.timestamp)
        const filtered = sorted.filter((e, i) => e.timestamp !== sorted?.[i - 1]?.timestamp)
        const fixed = fixBadTelemetryData(filtered)
        this.data = fixed
        this.hasFetched = true
        this.emit("dataChanged")
        if (zoomTo) {
            store.dispatch(
                zoomToShowPoints({
                    points: fixed.map((e) => [e.longitude, e.latitude]),
                })
            )
        }
    }

    override pushData(data: VideoControllerData) {
        if (!data.longitude || !data.latitude) {
            for (let i = this.data.length - 1; i >= 0; i--) {
                if (this.data[i].latitude && this.data[i].longitude) {
                    data.latitude = this.data[i].latitude
                    data.longitude = this.data[i].longitude
                    break
                }
            }
        }
        const lastDataItem = this.data.at(-1)
        if (!lastDataItem || (data.createdAt || 0) > (lastDataItem.createdAt || 0)) {
            this.data.push(data)
        } else {
            this.data = [...this.data, data].toSorted((a, b) => a.timestamp - b.timestamp)
        }
        this.emit("dataChanged")
    }

    isWaitingForStreamToStart() {
        const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
        return !!flight && selectLiveVideoStatus(flight) === LiveVideoStatus.LivePending
    }
}
